import { Provider } from 'store/api/enums';
import {
  Entity,
  Organisation,
  ReceiptEntityStatistics,
} from 'store/organisation/models';
import { entitySupportsDocumentScanning } from 'utils/captureTransactionsAccess';
import { getReceiptEntityStatsFromMap } from 'utils/receipt-statistics';

type CaptureEnabledOrg = Pick<
  Organisation,
  | 'providerName'
  | 'receiptScanStepsCompleted'
  | 'isGroup'
  | 'uuid'
  | 'connectionId'
  | 'excelFilesMetadata'
>;

function entitySupportsCaptureRules(providerName?: string | null): boolean {
  const provider = providerName?.toLowerCase().trim();
  if (!provider || provider === Provider.GROUP.toLowerCase()) {
    return false;
  }
  if (provider === Provider.EXCEL.toLowerCase()) {
    return false;
  }
  return (
    provider === Provider.RECEIPTS.toLowerCase() ||
    entitySupportsDocumentScanning(provider)
  );
}

export const isCaptureEnableExcludedProvider = (
  providerName?: string | null
): boolean => {
  const provider = providerName?.toLowerCase().trim();
  if (!provider) return true;
  return (
    provider === Provider.RECEIPTS.toLowerCase() ||
    provider === Provider.EXCEL.toLowerCase() ||
    provider === Provider.GROUP.toLowerCase()
  );
};

export const isReceiptsEntityProvider = (
  providerName?: string | null
): boolean =>
  providerName?.toLowerCase().trim() === Provider.RECEIPTS.toLowerCase();

export const shouldShowEntityCaptureEnable = (
  record: Pick<Entity, 'providerName' | 'receiptScanStepsCompleted'>,
  hasStats: boolean
): boolean => {
  if (isCaptureEnableExcludedProvider(record.providerName)) return false;
  if (!entitySupportsDocumentScanning(record.providerName)) return false;
  if (record.receiptScanStepsCompleted) return false;
  if (hasStats) return false;
  return true;
};

function organisationHasUploadedCaptureDocuments(
  org: Pick<Organisation, 'excelFilesMetadata'>
): boolean {
  return (
    Array.isArray(org.excelFilesMetadata) && org.excelFilesMetadata.length > 0
  );
}

export function resolveOrganisationCaptureStats(
  org: Pick<Organisation, 'uuid' | 'connectionId'>,
  statsByConnectionId?: Map<string, ReceiptEntityStatistics>
): ReceiptEntityStatistics | undefined {
  if (!statsByConnectionId?.size) return undefined;
  return getReceiptEntityStatsFromMap(statsByConnectionId, {
    connectionId: org.connectionId,
    organisationId: org.uuid,
  });
}

/**
 * Whether capture is active for an entity — aligned with:
 * - Entities list: inverse of {@link shouldShowEntityCaptureEnable} (uses receipt stats)
 * - Capture tab: onboarding complete (`receiptScanStepsCompleted`) or documents uploaded
 *
 * Uses organisation data already in Redux; optional stats map from
 * `getReceiptStatisticsAll` when the Entities/home views have loaded it (no extra fetch).
 */
export function entityHasCaptureEnabled(
  org: CaptureEnabledOrg,
  options?: {
    statsByConnectionId?: Map<string, ReceiptEntityStatistics>;
  }
): boolean {
  if (org.isGroup) return false;
  if (!entitySupportsCaptureRules(org.providerName)) return false;

  const hasStats = Boolean(
    resolveOrganisationCaptureStats(org, options?.statsByConnectionId)
  );
  const hasCaptureDocuments = organisationHasUploadedCaptureDocuments(org);

  return (
    org.receiptScanStepsCompleted === true ||
    hasStats ||
    hasCaptureDocuments
  );
}
